import React, { useEffect, useState, useMemo } from 'react';
import { Flag, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTheme } from '@/contexts/ThemeContext';
import api from '@/lib/api';

/**
 * Compact horizontal strip that shows the project's top-level milestones with
 * days-to-go badges. Renders above the Macro Schedule tabs so demo viewers see
 * the schedule spine at a glance (NTP → First Metal Tapping → Final Handover).
 *
 * Props:
 *   projectId: portfolio project id (e.g. "ALX-2025")
 */
export default function MilestoneBar({ projectId }) {
  const { isDark } = useTheme();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideCompleted, setHideCompleted] = useState(() => {
    try { return localStorage.getItem('milestone_hide_completed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('milestone_hide_completed', hideCompleted ? '1' : '0'); } catch { /* noop */ }
  }, [hideCompleted]);

  useEffect(() => {
    if (!projectId) return;
    const load = () => {
      setLoading(true);
      api.get(`/projects/${projectId}/schedule`)
        .then((r) => setMilestones(r.data?.milestones || []))
        .catch(() => setMilestones([]))
        .finally(() => setLoading(false));
    };
    load();
    // ── Auto-refresh: refetch when phase handoffs / macro schedule imports
    // dispatch this window event, so PMs never need to reload the page.
    const handler = () => load();
    window.addEventListener('milestones:refresh', handler);
    return () => window.removeEventListener('milestones:refresh', handler);
  }, [projectId]);

  const enriched = useMemo(() => {
    const today = new Date();
    return milestones.map((m) => {
      const planned = m.planned_date ? new Date(m.planned_date) : null;
      const actual  = m.actual_date  ? new Date(m.actual_date)  : null;
      const daysToGo = planned ? Math.round((planned - today) / (1000 * 60 * 60 * 24)) : null;
      const status = actual
        ? (m.variance_days > 3 ? 'completed_late' : 'completed_on_time')
        : (daysToGo != null && daysToGo < 0 ? 'overdue'
           : daysToGo != null && daysToGo <= 30 ? 'imminent' : 'future');
      return { ...m, planned, actual, daysToGo, status };
    });
  }, [milestones]);

  const completedCount = useMemo(
    () => enriched.filter((m) => m.status === 'completed_on_time' || m.status === 'completed_late').length,
    [enriched]
  );
  const visible = useMemo(
    () => hideCompleted ? enriched.filter((m) => m.status !== 'completed_on_time' && m.status !== 'completed_late') : enriched,
    [enriched, hideCompleted]
  );

  if (loading || enriched.length === 0) return null;

  const cfg = isDark ? {
    completed_on_time: { colour: '#10b981', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.35)', label: 'Done', Icon: CheckCircle2 },
    completed_late:    { colour: '#f59e0b', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.40)', label: 'Late', Icon: CheckCircle2 },
    overdue:           { colour: '#ef4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.45)',  label: 'Overdue', Icon: AlertTriangle },
    imminent:          { colour: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.45)', label: '≤30 days', Icon: Clock },
    future:            { colour: '#64748b', bg: 'rgba(100,116,139,0.10)',border: 'rgba(100,116,139,0.30)',label: 'Future', Icon: Flag },
  } : {
    completed_on_time: { colour: '#047857', bg: '#d1fae5', border: '#10b981', label: 'Done', Icon: CheckCircle2 },
    completed_late:    { colour: '#b45309', bg: '#fef3c7', border: '#f59e0b', label: 'Late', Icon: CheckCircle2 },
    overdue:           { colour: '#b91c1c', bg: '#fee2e2', border: '#ef4444', label: 'Overdue', Icon: AlertTriangle },
    imminent:          { colour: '#1d4ed8', bg: '#dbeafe', border: '#3b82f6', label: '≤30 days', Icon: Clock },
    future:            { colour: '#334155', bg: '#f1f5f9', border: '#cbd5e1', label: 'Future', Icon: Flag },
  };

  const containerCls = isDark
    ? 'bg-[#0c1222]/70 border-slate-700/50'
    : 'bg-white border-slate-200 shadow-sm';
  const headerCls = isDark ? 'text-slate-400' : 'text-slate-600';
  const codeCls   = isDark ? 'text-slate-300' : 'text-slate-800';
  const nameCls   = isDark ? 'text-slate-200' : 'text-slate-700';
  const dateCls   = isDark ? 'text-slate-500' : 'text-slate-500';

  return (
    <TooltipProvider>
      <div className={`${containerCls} border rounded-lg p-3 mb-3`} data-testid="milestone-bar">
        <div className="flex items-center gap-2 mb-2">
          <Flag className={`w-3.5 h-3.5 ${headerCls}`} />
          <span className={`text-xs uppercase tracking-wider ${headerCls} font-semibold`}>Project Milestones</span>
          <span className={`text-xs ${dateCls} ml-1`}>· {visible.length} of {enriched.length} gates{hideCompleted && completedCount > 0 ? ` · ${completedCount} completed hidden` : ''}</span>
          <label className="ml-auto flex items-center gap-1.5 cursor-pointer select-none" data-testid="milestone-hide-completed-toggle">
            <input
              type="checkbox"
              checked={hideCompleted}
              onChange={(e) => setHideCompleted(e.target.checked)}
              className="w-3 h-3 accent-blue-500 cursor-pointer"
            />
            <span className={`text-[11px] ${headerCls}`}>Hide completed</span>
          </label>
        </div>
        <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
          {visible.map((m) => {
            const c = cfg[m.status] || cfg.future;
            const Icon = c.Icon;
            return (
              <Tooltip key={m.milestone_id || m.code}>
                <TooltipTrigger asChild>
                  <div
                    className="flex flex-col justify-between rounded-md px-3 py-2 min-w-[140px] cursor-help"
                    style={{ backgroundColor: c.bg, border: `1px solid ${c.border}` }}
                    data-testid={`milestone-${m.code}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon className="w-3 h-3 shrink-0" style={{ color: c.colour }} />
                      <span className={`text-[11px] font-mono font-semibold ${codeCls}`}>{m.code}</span>
                    </div>
                    <div className={`text-[11px] ${nameCls} line-clamp-1 mt-1`} title={m.name}>{m.name}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-[10px] ${dateCls} font-mono`}>
                        {m.planned ? m.planned.toISOString().slice(0, 10) : '—'}
                      </span>
                      <span className="text-[10px] font-semibold" style={{ color: c.colour }}>
                        {m.actual
                          ? c.label
                          : (m.daysToGo != null
                              ? (m.daysToGo < 0
                                  ? `${Math.abs(m.daysToGo)}d late`
                                  : `${m.daysToGo}d to go`)
                              : '—')}
                      </span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <div className="space-y-1 text-xs">
                    <div className="font-semibold text-white">{m.code} · {m.name}</div>
                    <div>Planned: <span className="font-mono">{m.planned?.toISOString().slice(0, 10) || '—'}</span></div>
                    {m.actual && (
                      <div>Actual: <span className="font-mono">{m.actual.toISOString().slice(0, 10)}</span>
                        {' '}<span style={{ color: c.colour }}>({m.variance_days >= 0 ? '+' : ''}{m.variance_days ?? 0}d)</span></div>
                    )}
                    {!m.actual && m.daysToGo != null && (
                      <div>Time to go: <span style={{ color: c.colour }}>
                        {m.daysToGo < 0 ? `${Math.abs(m.daysToGo)} days overdue` : `${m.daysToGo} days remaining`}
                      </span></div>
                    )}
                    <div className="text-slate-400">Criticality: {m.criticality || 'major'}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
